import Button from '@components/Button';
import Column from '@components/Column';
import Image from '@components/Image';
import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import styled from '@emotion/styled';
import { RecentOrderRow } from '@models/order';
import { useFlow } from '@stackflow/react';
import { pressActionEffect } from '@styles/mixin';
import { useTranslation } from 'react-i18next';

import useDoRecentOrder from './hooks/useDoRecentOrder';

interface Props {
    order: RecentOrderRow;
    isSingle?: boolean;
}

function RecentOrderItem({ order, isSingle }:Props) {
    const {
        orderName, orderItems, shopName, orderNo,
    } = order;
    const { t } = useTranslation('main', { keyPrefix: 'recentOrders' });
    const thumbnail = orderItems?.[0]?.thumbNailImageUrl;
    const optionStr = orderItems?.[0]?.options
        ?.map((it) => `${it.optionName}(${it.qty}${t('unit')})`)
        .sort().join(', ');
    const handleOrder = useDoRecentOrder(order);
    const { push } = useFlow();
    return (
        <Container isSingle={isSingle}>
            <Summary fullWidth spacing={4} onClick={() => push('OrderLogActivity', { orderNo })}>
                <Thumbnail width={80}
                    height={80}
                    src={thumbnail} />
                <TextContainer spacing={2}>
                    <ClampText color={colors.textPrimary} fontWeight='medium'>
                        {orderName}
                    </ClampText>
                    <ClampText color={colors.textSecondary} size='body-long'>{optionStr}</ClampText>
                </TextContainer>
            </Summary>
            <Footer>
                <Text color={colors.gray6} size='body-long'>
                    {shopName}
                </Text>
                <OrderButton variant='outlined' size='xsmall' onClick={handleOrder}>
                    {t('order')}
                </OrderButton>
            </Footer>
        </Container>
    );
}

const Container = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isSingle',
})<{
    isSingle?: boolean;
}>`
    background-color: var(--color-background-pure);
    border-radius: var(--radius-2);
    flex-shrink: 0;
    width: ${({ isSingle }) => (isSingle ? 'calc(100vw - 32px)' : '300px')};
    max-width: ${({ isSingle }) => (isSingle ? 'calc(100vw - 32px)' : 'calc(100vw - var(--spacing-12))')};
    scroll-snap-stop: always;
    scroll-snap-align: start;
    ${pressActionEffect}
`;

const Summary = styled(Row)`
    padding: var(--spacing-4);
`;

const Thumbnail = styled(Image)`
    flex-shrink: 0;
    object-fit: cover;
`;

const TextContainer = styled(Column)`
    flex: 1;
    min-width: 0;
`;

const ClampText = styled(Text)`
    -webkit-box-orient: vertical;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    overflow: hidden;
    word-break: break-all;
`;

const Footer = styled.div`
    border-top: 1px solid var(--color-border-default);
    padding: var(--spacing-2) var(--spacing-4);
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const OrderButton = styled(Button)`
    color: var(--color-text-secondary);
    font-weight: 500;
    min-width: 56px;
`;

export default RecentOrderItem;
